import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Shield, Star, Trash2, Check, X, Plus, Edit3, ExternalLink, Search, Loader2, AlertCircle, MessageSquare, ChevronRight, Reply } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';
const DIFFICULTY_LABEL = { easy: 'Facile', medium: 'Intermedio', advanced: 'Avanzato' };
const DIFFICULTY_COLOR = { easy: '#22c55e', medium: '#f59e0b', advanced: '#ef4444' };
const CATEGORIES = ['pms', 'automazione', 'canali', 'accesso', 'contabilita', 'comunicazione'];

const EMPTY_FORM = { name: '', category: 'automazione', tagline: '', description: '', logo_color: '#6b7280', logo_letter: '', certified: false, featured: false, setup_difficulty: 'medium', setup_time_min: 30, website: '', tags: '' };

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs mb-1.5 font-medium" style={{ color: 'rgba(229,229,229,0.5)' }}>{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, ...props }) {
  return (
    <input value={value} onChange={onChange} {...props}
      className="w-full px-3 py-2 text-sm outline-none rounded-sm"
      style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }} />
  );
}

function StarDisplay({ value, size = 12 }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} size={size}
          fill={value >= i ? '#c9a84c' : 'none'}
          stroke={value >= i ? '#c9a84c' : 'rgba(229,229,229,0.2)'}
        />
      ))}
    </span>
  );
}

// ── Reviews Panel (SuperAdmin) ────────────────────────────────────────────────
function ReviewsPanel({ token }) {
  const headers = { 'X-Superadmin-Key': token };
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyDrafts, setReplyDrafts] = useState({});   // key: "intId|tenantSlug"
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/marketplace/admin/reviews`, { headers });
      setReviews(data.reviews || []);
    } catch { toast.error('Errore caricamento recensioni'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const key = (r) => `${r.integration_id}|${r.tenant_slug}`;

  const saveReply = async (review) => {
    const k = key(review);
    const text = (replyDrafts[k] ?? review.admin_reply ?? '').trim();
    if (!text) { toast.error('Scrivi una risposta prima di pubblicare'); return; }
    setSaving(s => ({ ...s, [k]: true }));
    try {
      await axios.post(
        `${API}/api/marketplace/admin/reviews/${review.integration_id}/${review.tenant_slug}/reply`,
        { reply_text: text },
        { headers },
      );
      toast.success('Risposta pubblicata');
      setReviews(prev => prev.map(r =>
        key(r) === k ? { ...r, admin_reply: text, admin_reply_at: new Date().toISOString() } : r
      ));
      setReplyDrafts(d => { const n = { ...d }; delete n[k]; return n; });
    } catch { toast.error('Errore salvataggio risposta'); }
    finally { setSaving(s => ({ ...s, [k]: false })); }
  };

  const deleteReply = async (review) => {
    if (!window.confirm('Rimuovere la risposta del team?')) return;
    const k = key(review);
    try {
      await axios.delete(
        `${API}/api/marketplace/admin/reviews/${review.integration_id}/${review.tenant_slug}/reply`,
        { headers },
      );
      toast.success('Risposta rimossa');
      setReviews(prev => prev.map(r =>
        key(r) === k ? { ...r, admin_reply: undefined, admin_reply_at: undefined } : r
      ));
    } catch { toast.error('Errore rimozione risposta'); }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-[#c9a84c]" /></div>;
  }

  if (reviews.length === 0) {
    return (
      <div className="text-center py-16 rounded-sm" style={{ border: '1px dashed var(--lux-border-2)' }}>
        <MessageSquare size={28} className="mx-auto mb-3 opacity-25" style={{ color: 'var(--lux-text-muted)' }} />
        <p className="text-sm" style={{ color: 'var(--lux-text-muted)' }}>Nessuna recensione ancora.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>
        {reviews.length} recensioni totali · {reviews.filter(r => r.admin_reply).length} con risposta del team
      </p>
      {reviews.map((review) => {
        const k = key(review);
        const draft = replyDrafts[k] ?? review.admin_reply ?? '';
        const hasReply = !!review.admin_reply;
        const isDirty = replyDrafts[k] !== undefined;

        return (
          <div key={k} className="rounded-sm overflow-hidden"
            style={{ border: '1px solid var(--lux-border-2)', background: 'var(--lux-surface-1)' }}>
            {/* Header recensione */}
            <div className="flex items-start gap-3 p-4" style={{ borderBottom: '1px solid var(--lux-border-1)' }}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <StarDisplay value={review.rating} size={12} />
                  <span className="font-medium text-sm" style={{ color: 'var(--lux-text)' }}>{review.tenant_name}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-text-muted-3)' }}>
                    {review.integration_name || review.integration_id}
                  </span>
                  {hasReply && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1"
                      style={{ background: 'rgba(201,168,76,0.1)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.2)' }}>
                      <Reply size={8} /> Risposta pubblicata
                    </span>
                  )}
                </div>
                {review.comment && (
                  <p className="text-xs italic" style={{ color: 'var(--lux-text-muted)', lineHeight: 1.5 }}>
                    "{review.comment}"
                  </p>
                )}
                <p className="text-[10px] mt-1" style={{ color: 'var(--lux-text-muted-3)' }}>
                  {new Date(review.updated_at || review.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
            </div>

            {/* Form risposta */}
            <div className="p-4 space-y-3" style={{ background: 'rgba(201,168,76,0.02)' }}>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded flex items-center justify-center text-[9px] font-bold" style={{ background: '#c9a84c', color: '#000' }}>O</div>
                <p className="text-xs font-medium" style={{ color: '#c9a84c' }}>Risposta Team Shangrilux</p>
                {hasReply && !isDirty && (
                  <button onClick={() => deleteReply(review)} title="Rimuovi risposta"
                    data-testid={`delete-reply-${k}`}
                    className="ml-auto text-gray-600 hover:text-red-400 transition-colors">
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
              <textarea
                value={draft}
                onChange={e => setReplyDrafts(d => ({ ...d, [k]: e.target.value.slice(0, 1000) }))}
                data-testid={`reply-textarea-${k}`}
                rows={3}
                placeholder="Scrivi la risposta pubblica del team Shangrilux a questa recensione..."
                className="w-full px-3 py-2 text-sm outline-none resize-none rounded-sm"
                style={{ background: 'var(--lux-surface-3)', border: `1px solid ${isDirty ? 'rgba(201,168,76,0.4)' : 'var(--lux-border-2)'}`, color: 'var(--lux-text)' }}
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={() => saveReply(review)}
                  disabled={saving[k] || !draft.trim()}
                  data-testid={`save-reply-${k}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition-all"
                  style={{
                    background: draft.trim() ? '#c9a84c' : 'var(--lux-surface-3)',
                    color: draft.trim() ? '#000' : 'var(--lux-text-muted-3)',
                    border: `1px solid ${draft.trim() ? '#c9a84c' : 'var(--lux-border-2)'}`,
                    cursor: draft.trim() ? 'pointer' : 'not-allowed',
                  }}
                >
                  {saving[k] ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                  {hasReply && !isDirty ? 'Modifica' : 'Pubblica risposta'}
                </button>
                {isDirty && (
                  <button
                    onClick={() => setReplyDrafts(d => { const n = { ...d }; delete n[k]; return n; })}
                    className="text-xs" style={{ color: 'var(--lux-text-muted-3)', background: 'none', border: 'none', cursor: 'pointer' }}>
                    Annulla
                  </button>
                )}
                <span className="ml-auto text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>
                  {draft.length}/1000
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main MarketplaceTab ────────────────────────────────────────────────────────
export function MarketplaceTab({ token }) {
  const [activeTab, setActiveTab] = useState('integrations');  // 'integrations' | 'reviews'
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const headers = { 'X-Superadmin-Key': token };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/marketplace/admin/all`, { headers });
      setIntegrations(data.integrations || []);
    } catch { toast.error('Errore caricamento marketplace'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY_FORM); setEditItem(null); setShowForm(true); };
  const openEdit = (item) => {
    setForm({ ...item, tags: (item.tags || []).join(', ') });
    setEditItem(item.integration_id);
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Inserisci il nome.'); return; }
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [], setup_time_min: parseInt(form.setup_time_min) || 30 };
      if (editItem) {
        await axios.patch(`${API}/api/marketplace/admin/${editItem}`, payload, { headers });
        toast.success('Integrazione aggiornata.');
      } else {
        await axios.post(`${API}/api/marketplace/admin`, payload, { headers });
        toast.success('Integrazione aggiunta.');
      }
      setShowForm(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel salvataggio.'); }
    finally { setSaving(false); }
  };

  const toggleField = async (item, field) => {
    try {
      await axios.patch(`${API}/api/marketplace/admin/${item.integration_id}`, { [field]: !item[field] }, { headers });
      setIntegrations(prev => prev.map(i => i.integration_id === item.integration_id ? { ...i, [field]: !i[field] } : i));
    } catch { toast.error('Errore aggiornamento.'); }
  };

  const approve = async (item) => {
    try {
      await axios.post(`${API}/api/marketplace/admin/${item.integration_id}/approve`, {}, { headers });
      setIntegrations(prev => prev.map(i => i.integration_id === item.integration_id ? { ...i, approved: true } : i));
      toast.success('Integrazione approvata e visibile nel marketplace.');
    } catch { toast.error('Errore approvazione.'); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Eliminare "${item.name}"?`)) return;
    try {
      await axios.delete(`${API}/api/marketplace/admin/${item.integration_id}`, { headers });
      setIntegrations(prev => prev.filter(i => i.integration_id !== item.integration_id));
      toast.success('Integrazione eliminata.');
    } catch { toast.error('Errore eliminazione.'); }
  };

  const filtered = integrations.filter(i => !search || i.name.toLowerCase().includes(search.toLowerCase()));
  const pending = filtered.filter(i => !i.approved);
  const approved = filtered.filter(i => i.approved);

  return (
    <div data-testid="marketplace-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl font-light" style={{ color: 'var(--lux-text)' }}>Marketplace Integrazioni</h2>
          <p className="text-xs mt-1" style={{ color: 'var(--lux-text-muted)' }}>
            {integrations.length} integrazioni · {integrations.filter(i => i.certified).length} certificate · {pending.length} in attesa approvazione
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Tab toggle */}
          <div className="flex rounded-sm overflow-hidden" style={{ border: '1px solid var(--lux-border-2)' }}>
            <button
              onClick={() => setActiveTab('integrations')}
              data-testid="tab-integrations"
              className="px-3 py-1.5 text-xs transition-all"
              style={{
                background: activeTab === 'integrations' ? '#c9a84c' : 'var(--lux-surface-2)',
                color: activeTab === 'integrations' ? '#000' : 'var(--lux-text-muted)',
                fontWeight: activeTab === 'integrations' ? 600 : 400,
                border: 'none', cursor: 'pointer',
              }}
            >Integrazioni</button>
            <button
              onClick={() => setActiveTab('reviews')}
              data-testid="tab-reviews"
              className="px-3 py-1.5 text-xs transition-all flex items-center gap-1"
              style={{
                background: activeTab === 'reviews' ? '#c9a84c' : 'var(--lux-surface-2)',
                color: activeTab === 'reviews' ? '#000' : 'var(--lux-text-muted)',
                fontWeight: activeTab === 'reviews' ? 600 : 400,
                border: 'none', cursor: 'pointer',
              }}
            >
              <MessageSquare size={11} /> Recensioni
            </button>
          </div>
          {activeTab === 'integrations' && (<>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--lux-text-muted-3)' }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca..." className="pl-7 pr-3 py-1.5 text-xs outline-none rounded-sm w-40" style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }} />
            </div>
            <button onClick={openNew} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm" style={{ background: '#c9a84c', color: '#000', border: 'none', cursor: 'pointer' }}>
              <Plus size={13} /> Aggiungi
            </button>
            <a href="/marketplace" target="_blank" className="flex items-center gap-1 text-xs" style={{ color: 'var(--lux-text-muted)', textDecoration: 'none' }}>
              <ExternalLink size={12} /> Vedi pubblica
            </a>
          </>)}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'reviews' ? (
        <ReviewsPanel token={token} />
      ) : (<>

      {/* Candidature in attesa */}
      {pending.length > 0 && (
        <div className="mb-6 p-4 rounded-sm" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)' }}>
          <p className="text-xs font-medium mb-3 flex items-center gap-2" style={{ color: '#f59e0b' }}>
            <AlertCircle size={13} /> {pending.length} candidature partner in attesa di approvazione
          </p>
          <div className="space-y-2">
            {pending.map(item => (
              <div key={item.integration_id} className="flex items-center justify-between p-3 rounded-sm" style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded flex items-center justify-center text-xs font-bold text-white" style={{ background: item.logo_color }}>{item.logo_letter}</div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--lux-text)' }}>{item.name}</p>
                    <p className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>da {item.submitted_by} · {item.category}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(item)} className="px-2.5 py-1 text-xs rounded-sm" style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)', cursor: 'pointer' }}>
                    <Edit3 size={11} />
                  </button>
                  <button onClick={() => approve(item)} className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-sm font-medium" style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.2)', cursor: 'pointer' }}>
                    <Check size={11} /> Approva
                  </button>
                  <button onClick={() => remove(item)} className="px-2.5 py-1 text-xs rounded-sm" style={{ background: 'rgba(239,68,68,0.08)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)', cursor: 'pointer' }}>
                    <X size={11} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista approvate */}
      {loading ? (
        <div className="text-center py-12" style={{ color: 'var(--lux-text-muted)' }}>
          <Loader2 size={20} className="animate-spin mx-auto mb-2" />Caricamento...
        </div>
      ) : (
        <div className="overflow-hidden rounded-sm" style={{ border: '1px solid var(--lux-border-1)' }}>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: 'var(--lux-surface-2)', borderBottom: '1px solid var(--lux-border-1)' }}>
                {['Integrazione', 'Categoria', 'Difficoltà', 'Certificato', 'In evidenza', 'Azioni'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--lux-text-muted-3)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {approved.map((item, i) => (
                <tr key={item.integration_id} style={{ background: i % 2 === 0 ? 'var(--lux-surface-1)' : 'var(--lux-surface-2)', borderBottom: '1px solid var(--lux-border-2)' }}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ background: item.logo_color }}>{item.logo_letter}</div>
                      <div>
                        <p className="font-medium" style={{ color: 'var(--lux-text)' }}>{item.name}</p>
                        <p className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>{item.tagline?.slice(0, 40)}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 capitalize" style={{ color: 'var(--lux-text-muted)' }}>{item.category}</td>
                  <td className="px-4 py-3">
                    <span style={{ color: DIFFICULTY_COLOR[item.setup_difficulty] }}>{DIFFICULTY_LABEL[item.setup_difficulty]}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleField(item, 'certified')} className="flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px]"
                      style={{ background: item.certified ? 'rgba(201,168,76,0.1)' : 'var(--lux-surface-3)', color: item.certified ? '#c9a84c' : 'var(--lux-text-muted-3)', border: `1px solid ${item.certified ? 'rgba(201,168,76,0.3)' : 'var(--lux-border-2)'}`, cursor: 'pointer' }}>
                      {item.certified ? <><Shield size={9} /> Sì</> : 'No'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleField(item, 'featured')} className="flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px]"
                      style={{ background: item.featured ? 'rgba(167,139,250,0.1)' : 'var(--lux-surface-3)', color: item.featured ? '#a78bfa' : 'var(--lux-text-muted-3)', border: `1px solid ${item.featured ? 'rgba(167,139,250,0.3)' : 'var(--lux-border-2)'}`, cursor: 'pointer' }}>
                      {item.featured ? <><Star size={9} /> Sì</> : 'No'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <button onClick={() => openEdit(item)} className="p-1.5 rounded-sm" style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)', cursor: 'pointer' }}>
                        <Edit3 size={11} />
                      </button>
                      <button onClick={() => remove(item)} className="p-1.5 rounded-sm" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)', color: '#ef4444', cursor: 'pointer' }}>
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {approved.length === 0 && (
            <div className="text-center py-8 text-xs" style={{ color: 'var(--lux-text-muted)' }}>Nessuna integrazione approvata.</div>
          )}
        </div>
      )}

      {/* Form add/edit */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.8)' }}>
          <div className="w-full max-w-lg rounded-sm overflow-hidden" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--lux-border-1)' }}>
              <p className="font-medium text-sm" style={{ color: 'var(--lux-text)' }}>{editItem ? 'Modifica integrazione' : 'Nuova integrazione'}</p>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', color: 'var(--lux-text-muted)', cursor: 'pointer' }}><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Nome *"><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Zapier" /></Field>
                <Field label="Lettera logo *"><Input value={form.logo_letter} onChange={e => setForm(f => ({ ...f, logo_letter: e.target.value.slice(0, 1).toUpperCase() }))} placeholder="Z" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Categoria">
                  <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full px-3 py-2 text-sm outline-none rounded-sm" style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }}>
                    {CATEGORIES.map(c => <option key={c} value={c} style={{ background: 'var(--lux-surface-1)' }}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Colore logo (hex)"><Input value={form.logo_color} onChange={e => setForm(f => ({ ...f, logo_color: e.target.value }))} placeholder="#ff4a00" /></Field>
              </div>
              <Field label="Tagline breve *"><Input value={form.tagline} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))} placeholder="Collega Shangrilux a 6.000+ app" /></Field>
              <Field label="Descrizione completa">
                <textarea rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Descrizione dell'integrazione..."
                  className="w-full px-3 py-2 text-sm outline-none resize-none rounded-sm"
                  style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }} />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Difficoltà">
                  <select value={form.setup_difficulty} onChange={e => setForm(f => ({ ...f, setup_difficulty: e.target.value }))} className="w-full px-3 py-2 text-sm outline-none rounded-sm" style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }}>
                    {['easy', 'medium', 'advanced'].map(d => <option key={d} value={d} style={{ background: 'var(--lux-surface-1)' }}>{DIFFICULTY_LABEL[d]}</option>)}
                  </select>
                </Field>
                <Field label="Tempo setup (min)"><Input type="number" value={form.setup_time_min} onChange={e => setForm(f => ({ ...f, setup_time_min: e.target.value }))} /></Field>
                <Field label="Sito web"><Input value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://..." /></Field>
              </div>
              <Field label="Tag (separati da virgola)"><Input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="pms, webhook, sync" /></Field>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer text-sm" style={{ color: 'var(--lux-text-muted)' }}>
                  <input type="checkbox" checked={form.certified} onChange={e => setForm(f => ({ ...f, certified: e.target.checked }))} />
                  Certificato Shangrilux
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm" style={{ color: 'var(--lux-text-muted)' }}>
                  <input type="checkbox" checked={form.featured} onChange={e => setForm(f => ({ ...f, featured: e.target.checked }))} />
                  In evidenza
                </label>
              </div>
            </div>
            <div className="flex gap-3 px-5 py-4 border-t" style={{ borderColor: 'var(--lux-border-1)' }}>
              <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-sm" style={{ background: '#c9a84c', color: '#000', border: 'none', cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} {editItem ? 'Salva modifiche' : 'Aggiungi'}
              </button>
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm rounded-sm" style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)', cursor: 'pointer' }}>Annulla</button>
            </div>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
